import { isImei } from "@/model/Imei"
import { createBarcodeDetector } from "@/util/barcode-scanner"
import type { BarcodeFormat } from "barcode-detector/ponyfill"
import { useEffect, useEffectEvent, type RefObject } from "react"

const SCAN_INTERVAL_MS = 250

// IMEI labels use a linear barcode (Code 128, sometimes Code 39). QR codes and the DataMatrix
// on the box are deliberately not decoded.
const IMEI_BARCODE_FORMATS: BarcodeFormat[] = ["code_128", "code_39"]

type UseBarcodeScannerParams = {
  videoRef: RefObject<HTMLVideoElement | null>
  enabled: boolean
  onDetected: (imei: string) => void
  // Called once when the detector cannot work at all (e.g. its wasm failed to load)
  onUnavailable: () => void
}

/**
 * Keeps decoding frames from the video while `enabled` and reports the first barcode that
 * holds an IMEI. Any other barcode in view is skipped and scanning continues.
 */
export const useBarcodeScanner = ({
  videoRef,
  enabled,
  onDetected,
  onUnavailable,
}: UseBarcodeScannerParams) => {
  const handleDetected = useEffectEvent(onDetected)
  const handleUnavailable = useEffectEvent(onUnavailable)

  useEffect(() => {
    if (!enabled) return
    const detector = createBarcodeDetector({ formats: IMEI_BARCODE_FORMATS })
    let stopped = false
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    const scanFrame = async () => {
      const video = videoRef.current
      const hasFrame = video !== null && video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA
      if (hasFrame) {
        try {
          const barcodes = await detector.detect(video)
          if (stopped) return
          const imei = barcodes.map((barcode) => barcode.rawValue).find(isImei)
          if (imei) {
            handleDetected(imei)
            return
          }
        } catch (error) {
          if (stopped) return
          // A frame the detector cannot read yet is skipped, anything else means it cannot scan
          const isUnreadableFrame =
            error instanceof DOMException && error.name === "InvalidStateError"
          if (!isUnreadableFrame) {
            handleUnavailable()
            return
          }
        }
      }
      if (!stopped) timeoutId = setTimeout(scanFrame, SCAN_INTERVAL_MS)
    }

    scanFrame()
    return () => {
      stopped = true
      clearTimeout(timeoutId)
    }
  }, [enabled, videoRef])
}
